import { FILTER_TYPE, PER_PAGE } from "@/constant/constants";
import { useCallback, useMemo, useRef } from "react";
import Actions from "./Actions";
import BasicBaseImportModal from "@/Modals/Profiles/BaseImport/BasicBaseImportModal";
import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import DataTable from "@/components/NewTable";
import { statusFilters } from "@/constant/statusConstants";
import { useGet } from "@/apiHooks/HttpFunctions";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import useUrlParams from "@/hooks/useUrlParams";

export default function BaseTable({
  label,
  apiURL,
  columns = [],
  ModalComponent,
  importData,
  enableSortData = false,
  enableStatus = true,
  confirmationModalTitleKey = "name",
  additionalFilters = [],
  enableExport = true,
}) {
  // Hooks
  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();

  // Refs
  const modalRef = useRef(null);
  const modalImportRef = useRef(null);

  // Derived States
  const filterData = useMemo(() => {
    const statusFiltersMap = statusFilters();
    return [
      enableStatus && {
        id: "filter[status]",
        label: "Status",
        type: FILTER_TYPE.SELECT,
        options: Object.entries(statusFiltersMap).map(([value, label]) => ({
          label,
          value,
        })),
      },
      ...additionalFilters,
    ].filter(Boolean);
  }, [enableStatus, additionalFilters]);

  // Callbacks
  const handleModalAction = useCallback(
    (type, data) => {
      openModalConfirmation({
        type,
        title: data[confirmationModalTitleKey] ?? "",
        id: data?.id,
        apiURL,
      });
    },
    [openModalConfirmation, confirmationModalTitleKey, apiURL]
  );

  const handleOnClickAdd = useCallback(() => modalRef.current?.open(), []);

  const handleOnClickImport = useCallback(
    () => modalImportRef.current?.open(),
    []
  );

  const handleOnClickEdit = useCallback(
    (data) => modalRef.current?.open({ id: data.id }),
    []
  );

  // Data Fetching
  const { data, isFetching, error, refetch } = useGet({
    apiURL,
    params: {
      ...params,
      perPage: params.perPage || PER_PAGE,
    },
  });

  // Merged Columns
  const mergedColumns = useMemo(() => {
    const actionsColumn = {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      enablePinning: true,
      cell: ({ row }) => (
        <Actions
          row={row}
          handleOnClickDelete={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.DELETE, data)
          }
          handleOnChangeStatus={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.CHANGE_STATUS, data)
          }
          handleOnClickEdit={handleOnClickEdit}
          enableStatus={enableStatus}
        />
      ),
    };

    return [actionsColumn, ...columns];
  }, [columns, handleOnClickEdit, enableStatus, handleModalAction]);

  // Render
  if (error) {
    return (
      <div>
        <p>Error loading data...</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <DataTable
        data={data}
        columns={mergedColumns}
        isLoading={isFetching}
        searchKey="filter[keyword]"
        addData={{
          label: `Add ${label}`,
          onClick: handleOnClickAdd,
        }}
        sortData={
          enableSortData && {
            api: apiURL,
          }
        }
        importData={
          importData && {
            onClick: handleOnClickImport,
          }
        }
        exportData={
          enableExport && {
            api: apiURL,
            label: `${label} List`,
          }
        }
        filterData={filterData}
      />
      {ModalComponent && <ModalComponent ref={modalRef} />}

      {importData && (
        <BasicBaseImportModal
          ref={modalImportRef}
          importData={importData}
          apiURL={apiURL}
        />
      )}
    </>
  );
}
